/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import dayjs from 'dayjs';

import { EventsService, FormHelpersService, ModalService } from 'app/shared/services';
import { Constants } from 'app/shared/models/constants.model';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: '[userEventModalGeneral]',
  templateUrl: './user-event-modal.component.html',
  styleUrls: ['./user-event-modal.component.scss'],
})
export class UserEventModalComponent implements OnInit {
  @Input() userEvents: any = [];
  @Input() event;
  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  isEditMode = false;
  loading = true;
  userEventForm: FormGroup;
  viewType: string;
  summaryErrorMessage = null;
  selectedEvent: any;
  eventLoading = false;
  eventInput$ = new Subject<string>();
  eventsList$: Observable<any>;
  readonly MODAL_TYPES = Constants.USER_EVENT_TYPES;

  constructor(
    private modalService: ModalService,
    public fhs: FormHelpersService,
    private eventsService: EventsService
  ) {
    this.userEventForm = new FormGroup({
      eventId: new FormControl(null),
      teamName: new FormControl(''),
      jerseyNo: new FormControl(''),
      eventName: new FormControl(''),
      eventDate: new FormControl(''),
      city: new FormControl(''),
      state: new FormControl(''),
    });
  }

  ngOnInit() {
    if (this.event) {
      this.isEditMode = true;
      this.userEventForm.controls.eventId.setValue(this.event.eventId ? this.event.eventId : null);
      this.userEventForm.controls.teamName.setValue(this.event.teamName ? `${this.event.teamName}` : '');
      this.userEventForm.controls.jerseyNo.setValue(this.event.jersey ? `${this.event.jersey}` : '');
      this.userEventForm.controls.eventName.setValue(this.event.name ? `${this.event.name}` : '');
      this.userEventForm.controls.eventDate.setValue(
        this.event.date ? `${dayjs(this.event.date).format('MM/DD/YY')}` : ''
      );
      this.userEventForm.controls.city.setValue(this.event.city ? `${this.event.city}` : '');
      this.userEventForm.controls.state.setValue(this.event.state ? `${this.event.state}` : '');
    }

    if (this.isEditMode && !this.event.eventId) {
      this.selectType(null, this.MODAL_TYPES.ADD, true);
    } else {
      this.selectType(null, this.MODAL_TYPES.SELECT, true);
    }
    this.loadEvents();
  }

  close() {
    this.modalService.hideModal();
    this.closeModal.emit(null);
  }

  onSubmit($event) {
    $event.preventDefault();
    if (this.userEventForm.invalid) {
      this.fhs.markFieldsAsDirty(this.userEventForm);
      this.summaryErrorMessage = 'Please fill all mandatory fields';
      return;
    }
    const formValues = this.userEventForm.value;
    if (!this.checkDuplicateNames(formValues)) {
      return;
    }
    this.summaryErrorMessage = null;

    let dateString;
    if (this.viewType === this.MODAL_TYPES.SELECT) {
      dateString = dayjs(this.selectedEvent.date).format('YYYY-MM-DD');
    }
    if (this.viewType === this.MODAL_TYPES.ADD) {
      const date = dayjs(formValues.eventDate, 'MM/DD/YY');
      if (!date.isValid() && this.isEditMode) {
        return;
      }

      dateString = date.format('YYYY-MM-DD');
    }

    const newEvent = {
      id: this.event && this.event.id ? this.event.id : null,
      city: formValues.city,
      date: dateString,
      event: null,
      jersey: formValues.jerseyNo,
      name: formValues.eventName,
      state: formValues.state,
      teamName: formValues.teamName,
    };

    if (formValues.eventId && this.viewType === this.MODAL_TYPES.SELECT) {
      newEvent.event = {
        id: this.selectedEvent.id,
      };
      newEvent.city = null;
      newEvent.state = null;
    }

    this.closeModal.emit(newEvent);
    this.modalService.hideModal();
  }

  selectType($event, newType: string, firstChange = false) {
    if ($event) {
      $event.preventDefault();
    }
    if (this.isEditMode && !firstChange) {
      return;
    }
    this.viewType = newType;
    switch (newType) {
      case this.MODAL_TYPES.ADD:
        this.userEventForm.get('eventId').clearValidators();
        this.userEventForm.get('eventId').setErrors(null);
        this.userEventForm.get('eventName').setValidators(Validators.required);
        this.userEventForm.get('eventDate').setValidators(Validators.required);
        break;

      case this.MODAL_TYPES.SELECT:
        this.userEventForm.get('eventId').setValidators(Validators.required);
        this.userEventForm.get('eventName').clearValidators();
        this.userEventForm.get('eventDate').clearValidators();
        break;
    }
    this.userEventForm.updateValueAndValidity();
  }

  formatEventsSearchResponse(response) {
    if (!response || !response.body || !response.body.hits || !response.body.hits.hits) {
      return [];
    }
    return response.body.hits.hits.map((eventResponse) => ({
      id: eventResponse._source.id,
      name: eventResponse._source.name,
      date: eventResponse._source.startdate,
    }));
  }

  onEventSelect(selectedOption) {
    if (selectedOption) {
      this.selectedEvent = { ...selectedOption };
    } else {
      this.selectedEvent = null;
    }
  }

  private loadEvents() {
    let initialEventList = [];
    const event = this.event;
    if (
      this.isEditMode &&
      this.viewType === this.MODAL_TYPES.SELECT &&
      event &&
      event.name &&
      event.eventId &&
      event.date
    ) {
      initialEventList = [{ id: this.event.eventId, name: this.event.name, date: this.event.date }];
      this.selectedEvent = { ...this.event, id: this.event.eventId };
    }

    this.eventsList$ = concat(
      of(initialEventList), // default items
      this.eventInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.eventLoading = true)),
        switchMap((term) =>
          this.eventsService.searchEvents(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.eventLoading = false)),
            map(this.formatEventsSearchResponse)
          )
        )
      )
    );
  }

  private checkDuplicateNames(formValues) {
    if (
      !this.isEditMode &&
      formValues.eventId &&
      this.viewType === this.MODAL_TYPES.SELECT &&
      this.userEvents.some((evt) => {
        if (evt.event && !evt.deleted) {
          return formValues.eventId === evt.event.id;
        } else {
          return false;
        }
      })
    ) {
      this.summaryErrorMessage = 'Selected event is already on your events list';
      return false;
    }

    if (
      !this.isEditMode &&
      formValues.eventName &&
      this.viewType === this.MODAL_TYPES.ADD &&
      this.userEvents.some((evt) => {
        if (evt.name) {
          return formValues.eventName === evt.name;
        } else {
          return false;
        }
      })
    ) {
      this.summaryErrorMessage = 'There is already an event with the same name on your list';
      return false;
    }
    return true;
  }
}
